import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { AiOutlineHome, AiOutlineDown, AiOutlineUp } from 'react-icons/ai';

/* Animations */
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const overlayFade = keyframes`
  from { opacity: 0; }
  to   { opacity: 0.5; }
`;

/* Layout */
const HeroWrapper = styled.section`
  position: relative;
  height: 100vh;
  min-height: 560px;
  background: url(${p => p.background}) center/cover no-repeat;
  display: flex;
  align-items: center;
  justify-content: flex-start;

  @media (max-width: 768px) {
    height: 70vh;
    min-height: 420px;
  }
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.5);
  opacity: 0;
  animation: ${overlayFade} 1s ease-out forwards;
  animation-delay: 0.2s;
  z-index: 1;
`;

const HeroContent = styled.div`
  position: absolute;
  z-index: 2;
  bottom: 15%;
  right: 40px;
  text-align: right;
  color: #fff;
  max-width: 1000px;

  .overlay h1 {
    font-size: 6rem;
    margin-bottom: 10px;
    white-space: pre-line;
    opacity: 0;
    animation: ${fadeInUp} 1s ease-out forwards;
    animation-delay: 0.6s;
    line-height: 1.05;
    letter-spacing: -0.02em;
  }
  .overlay p {
    font-size: 1.5rem;
    line-height: 1.6;
    white-space: pre-line;
    opacity: 0;
    animation: ${fadeInUp} 1s ease-out forwards;
    animation-delay: 1.2s;
  }

  @media (max-width: 1024px) {
    .overlay h1 { font-size: 4rem; }
  }
  @media (max-width: 768px) {
    right: 20px;
    left: 20px;
    bottom: 25%;             
    .overlay h1 { font-size: 2.05rem; line-height: 1.15; }
    .overlay p  { font-size: 0.85rem; }
  }
`;

/* Breadcrumbs */
const CrumbsBar = styled.nav`
  position: absolute;
  bottom: 30px;
  left: 40px;
  display: flex;
  align-items: center;
  gap: 14px;
  z-index: 3;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    left: 20px;
    right: 20px;
    gap: 10px;
  }
`;

const HomeCircle = styled(Link)`
  width: 48px; height: 48px;
  border-radius: 9999px;
  display: inline-flex; align-items: center; justify-content: center;
  background: #005BAC; color: #fff;
  box-shadow: 0 6px 16px rgba(0,0,0,0.18);
  text-decoration: none;
  &:hover { filter: brightness(0.95); }

  @media (max-width: 768px) {
    width: 40px; height: 40px;
  }
`;

const PillDropdown = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

const PillButton = styled.button`
  appearance: none;
  min-width: 220px;
  border: 1px solid rgba(255,255,255,0.55);
  background: rgba(0,0,0,0.25);
  color: #fff;
  display: inline-flex; align-items: center; justify-content: space-between;
  padding: 14px 18px;
  border-radius: 9999px;
  box-shadow: 0 6px 18px rgba(0,0,0,0.18);
  font-weight: 800; letter-spacing: .3px; cursor: pointer;
  backdrop-filter: blur(2px);

  &:hover { background: rgba(255,255,255,0.08); border-color: rgba(255,255,255,0.7); }

  @media (max-width: 768px) {
    min-width: 180px;
    padding: 12px 16px;
    font-weight: 600;
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  bottom: calc(100% + 8px);
  left: 0;
  min-width: 220px;
  border: 1px solid rgba(255,255,255,0.55);
  background: rgba(0,0,0,0.25);
  color: #fff;
  border-radius: 18px;
  box-shadow: 0 12px 28px rgba(0,0,0,0.24);
  padding: 8px;
  z-index: 10;

  opacity: ${p => (p.$open ? 1 : 0)};
  pointer-events: ${p => (p.$open ? 'auto' : 'none')};
  transform: translateY(${p => (p.$open ? '0' : '6px')});
  transition: opacity .16s ease, transform .16s ease;

  @media (max-width: 768px) { min-width: 180px; }
`;

const MenuItem = styled(Link)`
  display: block;
  padding: 10px 12px;
  border-radius: 18px;
  color: #fff;
  text-decoration: none; font-size: 14px;
  &:hover { font-weight: 700; }
`;

const HeroSection = ({
  title,
  subtitle,
  backgroundImage,
  breadcrumbs = [],
  categories = [],
}) => {
  const [open, setOpen] = useState(false);
  const ddRef = useRef(null);

  const homeCrumb = breadcrumbs?.[0]?.path ? breadcrumbs[0] : { label: '홈', path: '/' };
  const categoryLabel = (breadcrumbs?.[1]?.label) || '카테고리';
  const items = categories?.length
    ? categories
    : (breadcrumbs?.[1]?.path ? [{ label: breadcrumbs[1].label, path: breadcrumbs[1].path }] : [{ label: '전체제품', path: '/' }]);

  /* 외부 클릭 & ESC로 닫기 */
  useEffect(() => {
    const onDocClick = (e) => {
      if (!ddRef.current) return;
      if (!ddRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  return (
    <HeroWrapper background={backgroundImage}>
      <Overlay />

      <HeroContent>
        <div className="overlay">
          <h1>{title}</h1>
          <p>{subtitle}</p>
        </div>
      </HeroContent>

      <CrumbsBar aria-label="페이지 탐색" ref={ddRef}>
        <HomeCircle to={homeCrumb.path} aria-label="홈으로 이동">
          <AiOutlineHome size={22} />
        </HomeCircle>

        <PillDropdown>
          <PillButton
            type="button"
            aria-haspopup="menu"
            aria-expanded={open}
            aria-controls="hero-category-menu"
            onClick={() => setOpen(v => !v)}
          >
            <span>{categoryLabel.toUpperCase?.() || categoryLabel}</span>
            {open ? <AiOutlineDown size={16} /> : <AiOutlineUp size={16} />}
          </PillButton>

          <DropdownMenu id="hero-category-menu" role="menu" $open={open}>
            {items.map((c, i) => (
              <MenuItem key={i} to={c.path} role="menuitem" onClick={() => setOpen(false)}>
                {c.label}
              </MenuItem>
            ))}
          </DropdownMenu>
        </PillDropdown>
      </CrumbsBar>
    </HeroWrapper>
  );
};

export default HeroSection;
